// Pure helpers for the Algolia places search (FREAK_PLACES index).
// The index records carry `typeName`, `hasImage` and `_geoloc` (see api
// utils/freakPlaceAlgolia.ts), which these filters rely on.
// Mirrors mobile utils/placesSearch.ts.
//
// filters: { withPhotos: boolean, scope: 'anywhere' | 'nearMe' | 'mapArea', radiusKm: number }
// LatLng: { latitude, longitude }

export const PLACES_SEARCH_RADIUS_OPTIONS_KM = [5, 25, 50, 100];

export const DEFAULT_PLACES_SEARCH_FILTERS = {
  withPhotos: false,
  scope: 'anywhere',
  radiusKm: 25,
};

const isLatLng = (point) =>
  !!point && Number.isFinite(point.latitude) && Number.isFinite(point.longitude);

/** Builds the Algolia `Configure` params for the current filters. */
export const buildPlacesSearchParams = ({
  context,
  filters,
  userLocation,
  mapBounds,
}) => {
  const clauses = [`context:${context}`, 'public:true'];
  if (filters.withPhotos) {
    clauses.push('hasImage:true');
  }
  const params = {filters: clauses.join(' AND ')};

  if (filters.scope === 'nearMe' && isLatLng(userLocation)) {
    params.aroundLatLng = `${userLocation.latitude}, ${userLocation.longitude}`;
    params.aroundRadius = Math.round(filters.radiusKm * 1000);
  } else if (filters.scope === 'mapArea' && mapBounds && isLatLng(mapBounds.northEast) && isLatLng(mapBounds.southWest)) {
    params.insideBoundingBox = [[
      mapBounds.northEast.latitude,
      mapBounds.northEast.longitude,
      mapBounds.southWest.latitude,
      mapBounds.southWest.longitude,
    ]];
  }

  return params;
};

/** Number of active filters (excluding the search query) for the badge. */
export const countActivePlacesFilters = (filters, selectedTypesCount) =>
  selectedTypesCount + (filters.withPhotos ? 1 : 0) + (filters.scope !== 'anywhere' ? 1 : 0);

const EARTH_RADIUS_KM = 6371;
const toRad = (deg) => (deg * Math.PI) / 180;

/** Great-circle distance in km, or null when either point is missing. */
export const distanceKm = (from, to) => {
  if (!isLatLng(from) || !isLatLng(to)) {
    return null;
  }
  const dLat = toRad(to.latitude - from.latitude);
  const dLng = toRad(to.longitude - from.longitude);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(from.latitude)) * Math.cos(toRad(to.latitude)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(a));
};

/** "350 m", "4.2 km", "87 km". */
export const formatDistance = (km) => {
  if (km === null || !Number.isFinite(km)) {
    return null;
  }
  if (km < 1) {
    return `${Math.max(10, Math.round((km * 1000) / 10) * 10)} m`;
  }
  if (km < 10) {
    return `${km.toFixed(1)} km`;
  }
  return `${Math.round(km)} km`;
};

/** Coordinates of an Algolia place hit (`map.coordinates`), or null. */
export const hitCoordinates = (hit) => {
  const latitude = Number(hit?.map?.coordinates?.latitude ?? hit?._geoloc?.lat);
  const longitude = Number(hit?.map?.coordinates?.longitude ?? hit?._geoloc?.lng);
  return Number.isFinite(latitude) && Number.isFinite(longitude) ? {latitude, longitude} : null;
};

/** Leaflet LatLngBounds -> {northEast, southWest} used by buildPlacesSearchParams. */
export const leafletBoundsToMapBounds = (bounds) => {
  if (!bounds || typeof bounds.getNorthEast !== 'function') {
    return null;
  }
  const northEast = bounds.getNorthEast();
  const southWest = bounds.getSouthWest();
  return {
    northEast: {latitude: northEast.lat, longitude: northEast.lng},
    southWest: {latitude: southWest.lat, longitude: southWest.lng},
  };
};

/** Plural category for the "N places" label ('zero' | 'one' | 'few' | 'many'), Polish-aware. */
export const pluralCategory = (count, language) => {
  if (count === 0) return 'zero';
  if (count === 1) return 'one';
  if (language === 'pl') {
    const mod10 = count % 10;
    const mod100 = count % 100;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'few';
  }
  return 'many';
};

const HTML_ENTITIES = {
  '&nbsp;': ' ', '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&apos;': "'",
};

/** Turns a rich-text place description into plain text for one-line/clamped previews. */
export const htmlToPlainText = (html) => {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>|<\/(p|div|li|h[1-6]|pre)>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&(nbsp|amp|lt|gt|quot|apos|#39);/g, (entity) => HTML_ENTITIES[entity])
    .replace(/\s+/g, ' ')
    .trim();
};
